import _ from 'lodash'
import parseISO from 'date-fns/parseISO'
import React, { useRef } from 'react'
import Editor, { theme as EditorTheme } from '@gogovapps/rich-markdown-editor';
import { useDispatch } from 'react-redux';
import { Label, Checkbox, Input, Select, TextArea, Form } from 'semantic-ui-react'
import { Controller } from 'react-hook-form'
import DatePicker, { ReactDatePickerProps } from "react-datepicker";
import { SafeMoment } from './SafeMoment'
import { humanizeString } from '../lib/humanize-string'
import { s3Url } from '../utils/s3_url'
import { upload } from '../actions/upload'
import AppSettings from '../config'

import './ControlledForm.scss'
import "react-datepicker/dist/react-datepicker.css";

import { ControlledSelect } from './ControlledSelect'
export { ControlledSelect }

const Readonly = ({ as, value }:any) => {
  const isEmpty = [undefined, ''].includes(value)
  return React.createElement(as, !isEmpty ? {} : {style: { color: 'gray' }}, [isEmpty ? "N/A" : value])
}
const isErrorSelector = (errors:any) => (fieldName:string) => !!_.get(errors, `${fieldName}.message`);
const errorMessageSelector = (errors:any) => (fieldName:string) => isErrorSelector(fieldName) ? 
  _.get(errors, `${fieldName}.message`).replace(fieldName, humanizeString(fieldName)) : null;

interface IControlledWysiwyg {
  control: any
  name: string
  label?: string
  placeholder?: string
  errors?: any
  site: string
  folder: string
  bucket: string
  height?: string
}

export const ControlledWysiwyg = (props:IControlledWysiwyg) => {
  const dispatch = useDispatch();
  const isError = isErrorSelector(props.errors)(props.name)
  return (
    <Controller
      name={props.name}
      control={props.control}
      render={({ onChange, value, name }) => {
        const editorRef:any = useRef();
        return (
          <Form.Field error={isError}>
            {props.label && <label htmlFor={name}>{props.label}</label>}
            <div
              tabIndex={-1}
              role='textbox'
              onClick={(e) => {
                if((e.target as any).getAttribute('placeholder') === 'Search or paste a link…') {
                  return;
                }
                if(!editorRef.current.view.focused) {
                  editorRef.current.focusAtEnd()
                }
              }}
              style={{
                minHeight: props.height,
                  background: isError ? '#fff6f6' : 'white',
                  borderRadius: '4px',
                  border: isError ? '1px solid #e0b4b4' : '1px solid rgba(34,36,38,.15)',
                  color: isError ? '#9f3a38' : 'inherit',
                  padding: '6px',
                  marginLeft: '24px'
              }}>
              <Editor
                ref={editorRef}
                placeholder={props.placeholder}
                defaultValue={value}
                disabledToolbarItems={['mark']}
                disabledBlockMenuItems={['container_notice', 'table', 'checkbox_list']}
                theme={{
                  ...EditorTheme,
                  zIndex: '10000'
                } as any}
                uploadImage={async (file:any) => {
                  const uploadOptions = {
                    id: 'controlled-wysiwg',
                    site: props.site,
                    file: file,
                    folder: props.folder,
                    bucket: props.bucket
                  };
                  const uploadResponse = (await dispatch(upload(uploadOptions))) as any
                  return s3Url(uploadResponse.payload, AppSettings.CONTENT_URL)
                }}
                onChange={(getValue:any) => onChange(getValue().replace(/\n\\/g, "\n"))}
              />
            </div>
          </Form.Field>
        )
      }}
    />
  )
}

interface IControlledTextArea {
  control: any
  name: string
  label?: string
  placeholder?: string
  readonly?: boolean
  required?: boolean 
  errors?: any
  rows: number
  style?: any;
  rules?: any;
  defaultValue?: string
}

export const ControlledTextArea = (props:IControlledTextArea) => {
  const isError = isErrorSelector(props.errors)(props.name)
  return (
    <Controller
      name={props.name}
      control={props.control}
      defaultValue={props.defaultValue}
      rules={Object.assign({}, { required: props.required && 'This field is required' }, props.rules)}
      render={({ onChange, value, name }) => {
        return (
          <>
            {props.label && <label htmlFor={name}>{props.label}</label>}
            {props.readonly && <Readonly as='p' value={value || props.defaultValue}/>}
            {!props.readonly && <TextArea
              id={props.name}
              style={props.style}
              placeholder={props.placeholder}
              rows={props.rows}
              error={isError}
              name={name}
              value={value}
              onChange={(e, { value }) => (onChange(value))}
            />}
          </>
        )
      }}
    />
  )
}

interface IControlledInput {
  control: any
  name: string
  label?: string
  placeholder?: string
  errors?: any
  readonly?: boolean
  required?: boolean
  defaultValue?: any
  type?: string
  rules?: any
}

export const ControlledInput = (props:IControlledInput) => {
  const isError = isErrorSelector(props.errors)(props.name)
  return (
    <Controller
      name={props.name}
      control={props.control}
      defaultValue={props.defaultValue}
      rules={Object.assign({}, { required: props.required && 'This field is required' }, props.rules)}
      render={({ onChange, onBlur, value, name }) => {
        return (
          <>
            {props.label && <label htmlFor={name}>{props.label}</label>}
            {props.readonly && <Readonly as='span' value={value || props.defaultValue}/>}
            {!props.readonly && <Input
              id={props.name}
              type={props.type}
              placeholder={props.placeholder}
              error={isError}
              name={name}
              value={value}
              onBlur={onBlur}
              onChange={(e, { value }) => (onChange(value))}
            />}
            {isError && <Label basic prompt pointing='above'>{errorMessageSelector(props.errors)(name)}</Label>}
          </>
        )
      }}
    />
  )
}

interface IControlledTagInput {
  control: any
  name: string
  label?: string
  placeholder: string
  noResultsMessage?: string
  errors?: any
  rules?:any
  selectProps?: any
  required?: boolean
  defaultValue?: any
}

export const ControlledTagInput = (props:IControlledTagInput) => {
  const isError = isErrorSelector(props.errors)(props.name)
  return (
    <Controller
      name={props.name}
      control={props.control}
      rules={props.rules}
      defaultValue={props.defaultValue}
      render={({ onChange, value, name }) => {
        const options = value?.map((item:any) => ({key: item, text: item, value: item}))
        return (
          <>
            {props.label && <label htmlFor={name}>{props.label}</label>}
            <Select search selection multiple allowAdditions
              options={options}
              name={name}
              error={isError}
              value={value}
              placeholder={props.placeholder}
              required={props.required}
              noResultsMessage={props.noResultsMessage}
              onChange={(e:any, data:any) => onChange(data.value) }
              {...props.selectProps}
            />
          </>
        )
      }}>
    </Controller>
  );
}
 
interface IControlledCheckbox {
  name: string
  control?: any
  label?: string
  errors?: any
  disabled?: boolean
  required?: boolean
  readonly?: boolean
  toggle?: boolean
  defaultValue?: boolean
}

export const ControlledCheckbox = (props:IControlledCheckbox) => {
  const isError = isErrorSelector(props.errors)(props.name)
  return (
    <Controller
      name={props.name}
      control={props.control}
      rules={{ required: props.required && 'This field is required' }}
      defaultValue={props.defaultValue}
      render={({ onChange, value, name }) => {
        if(props.readonly) { return <Readonly as='p' value={(value || props.defaultValue || false).toString()}/> }
        return (
          <>
            <Checkbox
              name={name}
              label={props.label}
              error={isError}
              checked={value}
              toggle={props.toggle}
              disabled={props.disabled || props.readonly}
              onChange={(e, { checked }) => (onChange(checked))}
            />
          </>
        )
      }}
    />
  )
}

interface IControlledDatePicker extends Partial<ReactDatePickerProps>{
  control: any;
  name: string;
  label?: string;
  placeholder?: string;
  readonly?: boolean;
  required?: boolean;
  errors?: any;
  defaultValue?: any;
}

export const ControlledDatePicker = (props:IControlledDatePicker) => {
  const datePickerProps:Pick<IControlledDatePicker, keyof ReactDatePickerProps> = props;
  const isError = isErrorSelector(props.errors)(props.name)
  console.log(props.name, props.errors, isError)
  return (
    <Controller
      name={props.name}
      control={props.control}
      defaultValue={props.defaultValue}
      rules={{ required: props.required && 'This field is required' }}
      render={({ onChange, value, name }) => {
        const readonlyValue = (value || props.defaultValue) ? 
          <SafeMoment format={"MMM D, YYYY"}>{value || props.defaultValue}</SafeMoment> :
          <span style={{color:'gray'}}>N/A</span>
        return (
          <div className='ControlledFormDatePicker'>
            {props.label && <label htmlFor={name}>{props.label}</label>}
            {props.readonly && readonlyValue}
            {!props.readonly && <DatePicker
              selected={(value === '' || !value) ? null : (_.isString(value) ? parseISO(value) : value)}
              placeholderText={props.placeholder}
              onChange={(date:Date) => {
                onChange(date)
              }}
              {...datePickerProps}
              required={false}
            />}
            {isError && <Label basic prompt pointing='above'>{errorMessageSelector(props.errors)(name)}</Label>}
          </div>
        )
      }}
    />
  )

}
